import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Tab } from '@mui/material';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, CustomTabsV2 } from '../../../components/StyledComponents';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import CustomInput from '../../../components/CustomInput';
import CustomSelect from '../../../components/CustomSelect';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from '../../../context/AuthContext';

const DetailShiftRequestDialogAR = ({
   onClose,
   requestData,
   shiftsData,
   allUser,
   requestHistory,
   GroupData
}) => {

   const { genaralSettingDateFormat } = useAuth();

   const fromGroup = GroupData?.find(u => u.id === requestData.fromGroupId);
   const toGroup = GroupData?.find(u => u.id === requestData.toGroupId);

   const { t } = useTranslation();
   const MAX_REASON_LENGTH = 255;

   const getFullNameByEmail = useCallback(
      (email) => {
         if (!email) return "-";

         const normalizedEmail = String(email).trim().toLowerCase();

         const user = allUser.find(
            (u) =>
               u.email &&
               u.email.trim().toLowerCase() === normalizedEmail
         );

         return user?.fullName || email;
      },
      [allUser]
   );

   const sortedShifts = [...(shiftsData || [])].sort(
      (a, b) => b.shiftId - a.shiftId
   );

   const [formData, setFormData] = useState({
      userName: '',
      toUserName: '',
      fromDate: '',
      toDate: '',
      fromShift: '',
      toShift: '',
      requestReason: '',
      modifiedBy: '',
      responseReason: '',
   });

   useEffect(() => {
      if (!requestData) return;

      setFormData(prev => ({
         ...prev,
         userName: requestData?.fullNameFrom || '',
         toUserName: requestData?.fullNameTo || '',
         fromDate: requestData?.fromDate
            ? new Date(requestData?.fromDate)
            : null,
         toDate: requestData?.toDate
            ? new Date(requestData?.toDate)
            : null,
         fromShift: requestData?.fromShiftId || '',
         toShift: requestData?.toShiftId || '',
         requestReason: requestData.requestReason || '',
         modifiedBy: getFullNameByEmail(requestData.modifiedBy) || '-',
         responseReason: requestData.responseReason || '',
      }));
   }, [requestData, allUser, getFullNameByEmail]);

   /* ================= Handle Change ================= */
   const handleInputChange = (e) => {
      const { name, value } = e.target;

      if (name === 'description' && value.length > MAX_REASON_LENGTH) {
         return;
      }

      setFormData(prev => ({
         ...prev,
         [name]: value
      }));
   };

   const handleCloseDialog = () => {
      onClose();
   };

   const [tabValue, setTabValue] = useState(0);

   const handleChangeTab = useCallback((event, newValue) => {
      setTabValue(newValue);
   }, []);

   const CustomTabPanel = ({ children, value, index, ...other }) => (
      <div
         role="tabpanel"
         hidden={value !== index}
         id={`settings-${index}`}
         aria-labelledby={`settings-tab-${index}`}
         {...other}
      >
         {value === index && <div className='mt-3'>{children}</div>}
      </div>
   );

   const a11yProps = (index) => ({
      id: `user-tab-${index}`,
      'aria-controls': `user-tabpanel-${index}`,
   });

   const shiftMap = useMemo(() => {
      const map = {};
      (shiftsData || []).forEach(shift => {
         map[shift.shiftId] = shift.shiftName;
      });
      return map;
   }, [shiftsData]);

   const userMap = useMemo(() => {
      const map = {};
      (allUser || []).forEach(user => {
         map[user.userId] = user.fullName;
      });
      return map;
   }, [allUser]);

   const parsedHistory = useMemo(() => {
      return requestHistory?.map(item => {
         const data = item.newData ? JSON.parse(item.newData) : {};

         return {
            ...item,
            data: {
               ...data,
               fromShiftName: shiftMap[data.fromShiftId],
               toShiftName: shiftMap[data.toShiftId],
               userName: userMap[data.userId],
               swapUserName: userMap[data.toUserId],
               createdBy: item.createdBy
            }
         };
      }) || [];
   }, [requestHistory, shiftMap, userMap]);


   return (
      <CustomDialog open transitionDuration={300} maxWidth={'xl'} onClose={handleCloseDialog}>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold'>{t('button.detail')}</p>
               </div>
               <GrayIconButton onClick={handleCloseDialog}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>

         <div className='px-6'>
            <CustomTabsV2
               variant="scrollable"
               indicatorColor="#FFFFFF"
               value={tabValue}
               onChange={handleChangeTab}
            >
               <Tab label={t('button.detail')} {...a11yProps(0)} disableRipple />
               <Tab label={t('common.history')} {...a11yProps(1)} disableRipple />
            </CustomTabsV2>
         </div>

         <DialogContent>
            <CustomTabPanel value={tabValue} index={0}><div className='grid grid-cols-12 md:gap-x-10 gap-y-6'>

               {/* USER */}
               <div className='col-span-12 md:col-span-6'>
                  <label>
                     {t('common.your-group')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomInput
                     className='min-w-60'
                     value={fromGroup?.groupName}
                     readOnly={true}
                  />
               </div>

               {/* USER */}
               <div className='col-span-12 md:col-span-6'>
                  <label>
                     {t('common.swap-group')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomInput
                     className='min-w-60'
                     value={toGroup?.groupName}
                     readOnly={true}
                  />
               </div>

               {/* USER */}
               <div className='col-span-12 md:col-span-6'>
                  <label>
                     {t('common.staff')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomInput
                     className='min-w-60'
                     value={formData.userName}
                     readOnly={true}
                  />
               </div>

               {/* SWAP USER */}
               <div className='col-span-12 md:col-span-6'>
                  <label>
                     {t('common.staff-swap')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomInput
                     className='min-w-60'
                     value={formData.toUserName}
                     readOnly={true}
                  />
               </div>

               {/* CURRENT DATE */}
               <div className='col-span-12 md:col-span-6'>
                  <label>{t('common.date-work')}</label>
                  <div className="mt-2">
                     <CustomDateTimePicker
                        mode="single"
                        value={formData.fromDate}
                        onChange={(newValue) =>
                           setFormData(prev => ({ ...prev, currentDate: newValue }))
                        }
                        disableTime
                        showOnlyDate
                        disabled
                     />
                  </div>
               </div>

               {/* SWAP DATE */}
               <div className='col-span-12 md:col-span-6'>
                  <label>{t('common.swap-date-work')}</label>
                  <div className="mt-2">
                     <CustomDateTimePicker
                        mode="single"
                        value={formData.toDate}
                        onChange={(newValue) =>
                           setFormData(prev => ({ ...prev, swapDate: newValue }))
                        }
                        disableTime
                        showOnlyDate
                        disabled
                     />
                  </div>
               </div>

               {/* CURRENT SHIFT */}
               <div className='col-span-12 md:col-span-6'>
                  <label>
                     {t('common.current-shift')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomSelect
                     name='shift'
                     value={formData.fromShift}
                     onChange={handleInputChange}
                     options={sortedShifts}
                     className='mt-2'
                     dataType='shift'
                     readOnly={true}
                     disabled={true}
                  />
               </div>

               {/* SWAP SHIFT */}
               <div className='col-span-12 md:col-span-6'>
                  <label>
                     {t('common.swap-current-shift')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomSelect
                     name='swapShift'
                     value={formData.toShift}
                     onChange={handleInputChange}
                     options={sortedShifts}
                     className='mt-2'
                     dataType='shift'
                     readOnly={true}
                     disabled={true}
                  />
               </div>

               {/* DESCRIPTION */}
               <div className='col-span-12'>
                  <label>{t('common.swap-reason')}</label>
                  <CustomInput
                     name='description'
                     value={formData.requestReason}
                     handleInputChange={handleInputChange}
                     placeholder={t('common.description')}
                     readOnly={true}
                     disabled={true}
                  />
                  {/* <div
                     className={`flex justify-end text-xs mt-1 ${formData.requestReason.length >= MAX_REASON_LENGTH
                        ? "text-red-500"
                        : "text-gray-500"
                        }`}
                  >
                     {formData.requestReason.length}/{MAX_REASON_LENGTH}
                  </div> */}
               </div>

               <div className='col-span-12 mt-[-16px]'>
                  <label>
                     {t('common.approve-reject-by')}
                     <span className='text-red-500 ml-1'>*</span>
                  </label>
                  <CustomInput
                     className='min-w-60'
                     value={formData.modifiedBy}
                     readOnly={true}
                  />
               </div>

               <div className='col-span-12'>
                  <label>{t('common.approve-reject-reason')}</label>
                  <span className='text-red-500 ml-1'>*</span>
                  <CustomInput
                     name='reason'
                     value={formData.responseReason}
                     handleInputChange={handleInputChange}
                     placeholder={t('common.approve-reject-reason')}
                     readOnly={true}
                     disabled={true}
                  />
                  {/* <div
                     className={`flex justify-end text-xs mt-1 ${(formData.responseReason || "").length >= MAX_REASON_LENGTH
                        ? "text-red-500"
                        : "text-gray-500"
                        }`}
                  >
                     {(formData.responseReason || "").length}/{MAX_REASON_LENGTH}
                  </div> */}
               </div>

            </div></CustomTabPanel>

            <CustomTabPanel value={tabValue} index={1}>
               <div className="mt-4 relative">

                  {!parsedHistory || parsedHistory.length === 0 ? (
                     <div className="text-center text-gray-500 py-10">
                        {t("common.no-data")}
                     </div>
                  ) : (
                     parsedHistory.map((item, index) => {

                        const data = item.data || {};

                        return (
                           <div key={index} className="flex gap-4 relative pb-8">

                              {index !== parsedHistory.length - 1 && (
                                 <div className="absolute left-[19px] top-10 w-[2px] h-full bg-gray-200" />
                              )}

                              {/* Timeline icon */}
                              <div className="flex flex-col items-center z-10">
                                 <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center border
                           ${item.requestStatusName === "APPROVED"
                                          ? "bg-[#F0FDF4] border-[#E5E7EB]"
                                          : item.requestStatusName === "REJECTED"
                                             ? "bg-[#FEF2F2] border-[#E5E7EB]"
                                             : "bg-[#FEF2E6] border-[#E5E7EB]"
                                       }`}
                                 >
                                    <div
                                       className={`w-6 h-6 rounded-full flex items-center justify-center
                             ${item.requestStatusName === "APPROVED"
                                             ? "bg-green-500"
                                             : item.requestStatusName === "REJECTED" || item.requestStatusName === "PARTNER_REJECTED"
                                                ? "bg-red-500"
                                                : "bg-orange-400"
                                          }`}
                                    >
                                       {item.requestStatusName === "APPROVED" && (
                                          <DoneIcon sx={{ color: "#fff", fontSize: 16 }} />
                                       )}

                                       {(
                                          item.requestStatusName === "REJECTED" ||
                                          item.requestStatusName === "PARTNER_REJECTED"
                                       ) && (
                                             <ClearIcon sx={{ color: "#fff", fontSize: 16 }} />
                                          )}

                                       {(item.requestStatusName === "PARTNER_PENDING" ||
                                          item.requestStatusName === "PENDING") && (
                                             <AccessTimeIcon sx={{ color: "#fff", fontSize: 16 }} />
                                          )}
                                    </div>
                                 </div>
                              </div>

                              {/* Content */}
                              <div className="flex-1 px-4 bg-white">

                                 {/* Title */}
                                 <p className="font-semibold text-gray-900">
                                    {
                                       getFullNameByEmail(data.createdBy)
                                    }{" "}

                                    <span className="font-normal text-gray-700">
                                       {item.requestStatusName === "APPROVED"
                                          ? t("common.approvedRequest")
                                          : item.requestStatusName === "REJECTED" || item.requestStatusName === "PARTNER_REJECTED"
                                             ? t("common.rejectedRequest")
                                             : item.requestStatusName === "PENDING"
                                                ? t("common.pending-partner")
                                                : t("common.sentRequest")}
                                    </span>
                                 </p>

                                 <p className="text-sm text-gray-600 mt-1">
                                    <span className="text-gray-500">{t("common.date")}:</span>{" "}
                                    {item.requestStatusName === "PENDING"
                                       ? `${data.toDate ? format(new Date(data.toDate), genaralSettingDateFormat) : "-"} → ${data.fromDate ? format(new Date(data.fromDate), genaralSettingDateFormat) : "-"
                                       }`
                                       : `${data.fromDate ? format(new Date(data.fromDate), genaralSettingDateFormat) : "-"} → ${data.toDate ? format(new Date(data.toDate), genaralSettingDateFormat) : "-"
                                       }`
                                    }
                                 </p>

                                 <p className="text-sm text-gray-600">
                                    <span className="text-gray-500">{t("common.shift")}:</span>{" "}
                                    {item.requestStatusName === "PENDING"
                                       ? `${data.toShiftName || "-"} → ${data.fromShiftName || "-"}`
                                       : `${data.fromShiftName || "-"} → ${data.toShiftName || "-"}`
                                    }
                                 </p>

                                 <p className="text-sm text-gray-600">
                                    <span className="text-gray-500">
                                       {t("common.swapWith")}:
                                    </span>{" "}
                                    {item.requestStatusName === "APPROVED" ? (
                                       <>
                                          {(data.userName || fromGroup?.groupName || "-")}
                                          {" → "}
                                          {(data.swapUserName || toGroup?.groupName || "-")}
                                       </>
                                    ) : item.requestStatusName === "PARTNER_PENDING" ? (
                                       (data.swapUserName || toGroup?.groupName || "-")
                                    ) : (
                                       (data.userName || fromGroup?.groupName || "-")
                                    )}
                                 </p>
                                 {/* Reason */}
                                 <p className="text-sm text-gray-600">
                                    <span className="text-gray-500">
                                       {t("common.reason")}:
                                    </span>{" "}
                                    {item.requestStatusName === "PARTNER_PENDING"
                                       ? item.requestReason
                                       : item.requestStatusName === "APPROVED"
                                          ? (item.responseReason || item.requestReason)
                                          : (item.responseReason || item.requestReason)}
                                 </p>

                              </div>
                           </div>
                        );
                     })
                  )}

               </div>
            </CustomTabPanel>

         </DialogContent>

         <DialogActions>
            {/* <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                  <CloseIcon />
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>

               <ControlpanelErrorButton
               >
                  <p>{t('button.reject')}</p>
               </ControlpanelErrorButton>
               <ControlpanelSuccessButton
                  style={{
                     color: '#fff',
                     opacity: !formData.reason ? '0.5' : '1',
                  }}
               >
                  <p>{t('button.approve')}</p>
               </ControlpanelSuccessButton>
            </div> */}
         </DialogActions>
      </CustomDialog>
   );
};

export default DetailShiftRequestDialogAR;
